import React from 'react';
import type { GetServerSideProps } from 'next';
import { logger } from '@portal/env/utils/serversideLogger';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import { BasketLayout } from '@portal/ui/bw-ui/checkout/BasketLayout';
import { UnpaidBookingReminder } from '@bw-ui/checkout/UnpaidBookingReminder';
import { CheckoutAside } from '@bw-ui/checkout/CheckoutAside';
import { BasketList } from '@bw-ui/basket/BasketList';
import { BasketItemCard } from '@bw-ui/basket/BasketItemCard';
import { BasketActions } from '@bw-ui/basket/BasketActions';
import { EmptyBasket } from '@bw-ui/basket/EmptyBasket';
import { useBasket } from '@bw-ui/hooks/useBasket';
import { useAuth } from '@bw-ui/hooks/useAuth';
import { useApp } from '@bw-ui/hooks/useApp';
import { useBookings } from '@bw-ui/hooks/useBookings';
import { useInvoicePreview } from '@bw-ui/hooks/useInvoicePreview';
import { useTeamDiscount } from '@bw-ui/hooks/useTeamDiscount';
import { useAccountBalance } from '@bw-ui/hooks/useAccountBalance';
import { transformBasketItem, validateDiscountResult } from '@bw-ui/basket';
import { BookingDetails } from 'ui/_pages/bookings/calendar/BookingDetails';
import { getErrorDescription } from 'ui/_pages/bookings/BookingErrorCodes';
import { routes } from '@portal/env/routes';
import { defaultServices } from '@portal/env/services/defaultServices';
import { useCreateBasketInvoice } from '@portal/env/hooks/payments/useCreateBasketInvoice';
import { useBookingCancellation } from '@portal/env/hooks/useBookingCancellation';

/**
 * New Basket Page using bw-ui components
 * Access at /Checkout
 */
const CheckoutPage = observer(() => {
  const router = useRouter();
  const { data, actions } = useBasket();
  const { isLoggedIn, accessToken, host } = useAuth();
  const { resourceImageBaseUrl } = useApp();
  const { store: bookingsStore } = useBookings();
  const { teamDiscounts } = useTeamDiscount();
  const { accountBalance, isLoading: isLoadingBalance } =
    useAccountBalance(isLoggedIn);
  const [discountError, setDiscountError] = React.useState('');

  const {
    summary,
    invoicePreview,
    isLoading: isLoadingPreview,
    error: previewError,
    refetch: refetchPreview,
    noPreviewAvailable,
  } = useInvoicePreview(isLoggedIn, teamDiscounts, accountBalance);

  const createBasketInvoice = useCreateBasketInvoice({
    invoiceSummary: summary,
    invoicePreview,
  });

  const items = data.items.map((item, index) =>
    transformBasketItem(item, index, {
      resourceImageBaseUrl,
      invoicePreview,
      teamDiscounts,
      isLoadingInvoice: isLoadingPreview,
    }),
  );

  const { cancellationPolicyMinutes: longestPolicy, canCancelBooking } =
    useBookingCancellation(data, accessToken ?? '', host ?? '');

  // When preview invoice returns error from API, show it as an error
  const invoiceError = invoicePreview?.Message
    ? getErrorDescription(invoicePreview.Message, '', (s: string) => s)
    : '';
  const error =
    invoiceError || previewError || createBasketInvoice.error?.message;

  const handleRemove = (id: string) => {
    const index = Number(id.split('-')[0]);
    const item = data.items[index];
    if (item) {
      // we dont want to trigger a preview invoice here, the hook handles that from the change in count
      actions.removeFromBasket({ item, previewInvoice: false });
    }
  };

  const handleEdit = (id: string) => {
    const index = Number(id.split('-')[0]);
    const item = data.items[index];
    if (!item) return;

    switch (item.type) {
      case 'booking':
        bookingsStore.loadBasketBooking(item.data);
        break;
      case 'event':
        router
          .push(
            routes.tickets_by_id('[event_id]'),
            routes.tickets_by_id(item.data),
          )
          .catch((e) => {
            console.error('Error navigating to tickets', e);
          });
        break;
      case 'contract':
        router.push(routes.plan).catch((e) => {
          console.error('Error navigating to plan', e);
        });
        break;
      case 'product':
        router.push(routes.store).catch((e) => {
          console.error('Error navigating to product', e);
        });
        break;
    }
  };

  const handleModalClose = () => {
    refetchPreview().catch((e) => {
      console.error('Error refetching invoice preview after modal close', e);
    });
  };

  /**
   * Apply discount code and validate the result so that we can display appropriate error messages.
   * If the discount is invalid, we need to clear the discount code and re-fetch the invoice preview.
   */
  const handleApplyDiscount = async (code: string) => {
    setDiscountError('');
    actions.applyDiscount(code);

    const preview = await refetchPreview();
    if (!preview) {
      actions.applyDiscount(null);
      setDiscountError('Something went wrong. Please try again.');
      return;
    }

    const discountValidationError = validateDiscountResult(preview, code);
    if (discountValidationError) {
      setDiscountError(discountValidationError);
      actions.applyDiscount(null);
      refetchPreview().catch((e) => {
        console.error('Error refetching invoice preview after modal close', e);
      });
    }
  };

  const isEmpty = data.items.length === 0;
  const hasResources = data.items.some((item) => item.type === 'booking');
  const { subtotal, tax, total, discountLines, accountCredits, amountDue } =
    summary;

  return (
    <>
      <BookingDetails close={handleModalClose} isEditingBasket />
      <BasketLayout headerChildren={<UnpaidBookingReminder />}>
        {isEmpty ? (
          <EmptyBasket
            onBack={() => router.back()}
            onAddBooking={() => router.push(routes.resources)}
            onAddEvent={() => router.push(routes.events)}
            onDashboard={() => router.push(routes.home)}
          />
        ) : (
          <>
            <BasketList
              onBack={() => router.back()}
              hasResources={hasResources}
            >
              {items.map((item) => (
                <BasketItemCard
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onRemove={handleRemove}
                />
              ))}
              <BasketActions
                onAdd={() => router.push(routes.resources)}
                onClear={() => actions.clearBasket()}
              />
            </BasketList>

            <CheckoutAside
              isLoggedIn={isLoggedIn}
              isLoadingPreview={isLoadingPreview}
              isLoadingBalance={isLoadingBalance}
              invoicePreview={invoicePreview}
              noPreviewAvailable={noPreviewAvailable}
              summary={{
                subtotal,
                tax,
                total,
                discountLines,
                accountCredits,
                amountDue,
              }}
              onProceed={() => createBasketInvoice.mutate()}
              onProceedLoading={createBasketInvoice.isPending}
              cancellationPolicyMinutes={longestPolicy}
              canCancelBooking={canCancelBooking}
              error={error}
              onApplyDiscount={handleApplyDiscount}
              appliedCode={data.discountCode ?? undefined}
              discountError={discountError || undefined}
              signInHref={`${routes.login}?returnurl=${encodeURIComponent(routes.checkout)}`}
              createAccountHref={`${routes.signup}?returnurl=${encodeURIComponent(routes.checkout)}`}
            />
          </>
        )}
      </BasketLayout>
    </>
  );
});

export default CheckoutPage;

export const getServerSideProps: GetServerSideProps = async () => {
  logger.info('Checkout page visited');

  const services = await defaultServices();
  const hasStripePaymentsFeature = await services.featureFlags.hasFeature(
    'featureStripePayments',
  );

  if (!hasStripePaymentsFeature) {
    return { notFound: true };
  }

  return { props: {} };
};
